"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const faqs = [{
  question: "How to optimize budget hotel revenue?",
  answer: "Optimizing budget hotel revenue requires a strategic mix of dynamic pricing, expanding your OTA (Online Travel Agency) presence, and actively managing your online reputation. At HotelRevBaBa, we implement data-driven strategies to ensure you capture maximum demand without sacrificing your bottom line, focusing on the right channel mix for your specific location."
}, {
  question: "Priority: occupancy or ADR?",
  answer: "For budget hotels, achieving a high baseline occupancy is often the initial priority to cover fixed operational costs. However, once that baseline is established, the focus must shift to strategically increasing Average Daily Rate (ADR) during peak demand periods. A balanced approach that yields the highest RevPAR (Revenue Per Available Room) is the ultimate goal."
}, {
  question: "How to compete on price?",
  answer: "Competing solely by lowering prices is a race to the bottom that hurts profitability. Instead, we recommend competing on value and visibility. By optimizing your OTA listings, highlighting unique amenities, maintaining strict rate parity, and utilizing targeted promotions, you can maintain competitive pricing while protecting your profit margins."
}, {
  question: "Can we add more OTAs?",
  answer: "Yes, diversifying your distribution channels is crucial for visibility. However, it's not just about adding every OTA available; it's about adding the right ones. We analyze your target market and geographic location to recommend and implement the most effective OTAs for your property, ensuring maximum reach without overwhelming your front desk operations."
}, {
  question: "What's the payback for budget hotels?",
  answer: "The payback period for professional revenue management initiatives in budget hotels can be surprisingly fast. By implementing dynamic pricing models and optimizing OTA listings, many of our clients see a noticeable increase in revenue and bookings within the first 30 to 60 days of implementation."
}];
export default function RevenueFaq() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: listRef,
    isVisible: isListVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="budgethotelrevenuefaq" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      {}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />

      <div className="container mx-auto px-6 md:px-8 max-w-4xl">
        <div ref={headerRef} className={`text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4">
            Revenue Insights
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Budget Hotel Revenue FAQ
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">
            Direct answers to the most common questions about maximizing
            profitability and demand for budget properties.
          </p>
        </div>

        <div ref={listRef} className={`transition-all duration-700 delay-200 ease-out ${isListVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-card rounded-xl shadow-sm border border-border p-6 md:p-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-border last:border-0">
                  <AccordionTrigger className="text-left font-default text-base md:text-lg font-semibold text-foreground hover:text-secondary transition-colors py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}